import React, { useEffect, useRef, useState } from "react";
import {
    FilterList,
} from "@mui/icons-material";
import {
    Button,
    Grid,
    styled,
    Snackbar,
    Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import Footer from "../../../Components/Footer";
import VisualizationGraph from "../components/VisualizationGraph";
import useFilterState from "../hooks/useFilterState";
import useReportData from "../hooks/useReportData";
import ReportFilter from "../components/ReportFilter";
import ActiveFilters from "../components/ActiveFilters";
import ReportTable from "../components/ReportTable";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const initialFilters = {
    sites: [],
    states: [],
    companies: [], // Added companies filter
    financialYear: null,
    startDateTime: "",
    endDateTime: "",
};

const buildQueryString = (filterValues) => {
    const queryParts = [];

    // Add sites parameters
    filterValues.sites?.forEach((site, index) => {
        queryParts.push(`stores[${index}]=${site.id}`);
    });

    // Add states parameters
    filterValues.states?.forEach((state, index) => {
        queryParts.push(`states[${index}]=${state.state}`);
    });

    // Add companies parameters
    filterValues.companies?.forEach((company, index) => {
        queryParts.push(`company_id[${index}]=${company.id}`);
    });

    // Add financial year
    if (filterValues.financialYear) {
        queryParts.push(`financial_year=${filterValues.financialYear.id}`);
    }

    // Add date range
    if (filterValues.startDateTime && filterValues.endDateTime) {
        queryParts.push(`start_date=${filterValues.startDateTime}`);
        queryParts.push(`end_date=${filterValues.endDateTime}`);
    }

    // Return with leading '&' if parameters exist
    return queryParts.length ? `&${queryParts.join('&')}` : '';
};

const Header = styled("div")(({ theme }) => ({
    "& h1": {
        color: theme.palette.primary.dark,
        margin: "0",
    },
}));

const ReedemedBenefits = (props) => {
    const {
        getTokenRefreshed: refresh,
        open: tokenOpen,
        overlay,
        setOpen: setTokenOpen,
        message: tokenMessage,
        messageState: tokenMessageState
    } = useTokenRefresh();

    const [filterOpen, setFilterOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [messageState, setMessageState] = useState("");

    const {
        filters,
        appliedFilters,
        updateFilter,
        resetFilters,
        applyFilters,
        hasActiveFilters
    } = useFilterState(initialFilters);

    const { reportData, title, loading, error, startDate, endDate, refetch } = useReportData(props?.request_name, appliedFilters, buildQueryString);

    const initialDatesSet = useRef(false);

    useEffect(() => {
        if (!initialDatesSet.current && startDate && endDate) {
            const formattedStart = startDate.replace(" ", "T").slice(0, 16);
            const formattedEnd = endDate.replace(" ", "T").slice(0, 16);

            updateFilter("startDateTime", formattedStart);
            updateFilter("endDateTime", formattedEnd);

            initialDatesSet.current = true;
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (error) {
            setMessageState("error");
            setMessage(error.response?.data?.message || "An error occurred");
            setOpen(true);
        }
    }, [error]);

    // Define columns for each table type
    const stateColumns = [
        { id: "state_name", name: "State" },
        { id: "total_birthday_redemptions", name: "Total Birthday Redeemed" },
        { id: "total_birthday_redemption_amount", name: "Total Birthday Redeemed Amount", formatCurrency: true },
        { id: "total_cashback_redemptions", name: "Total Cashback Redeemed" },
        { id: "total_cashback_amount", name: "Total Cashback Amount", formatCurrency: true },
        { id: "total_sales", name: "Total Sales", formatCurrency: true }
    ];

    const storeColumns = [
        { id: "store_name", name: "Store" },
        { id: "total_birthday_redemptions", name: "Total Birthday Redeemed" },
        { id: "total_birthday_redemption_amount", name: "Total Birthday Redeemed Amount", formatCurrency: true },
        { id: "total_cashback_redemptions", name: "Total Cashback Redeemed" },
        { id: "total_cashback_amount", name: "Total Cashback Amount", formatCurrency: true },
        { id: "total_sales", name: "Total Sales", formatCurrency: true }
    ];

    const companyColumns = [
        { id: "company_name", name: "Company" },
        { id: "total_amount_of_redeemed_benefits", name: "Total Amount of Redeemed Benefits", formatCurrency: true },
        { id: "total_amount", name: "Total Amount", formatCurrency: true }
    ];

    const handleFilterSubmit = () => {
        applyFilters();
    };

    const handleFilterReset = () => {
        resetFilters();
    };

    const isRemoving = useRef(false);

    const removeFilter = (filterName) => {
        isRemoving.current = true;

        if (filterName === "dateRange") {
            // Clear both date properties
            updateFilter("startDateTime", "");
            updateFilter("endDateTime", "");
        } else {
            // Handle other filter types
            updateFilter(
                filterName,
                filterName === "sites"
                    ? []
                    : filterName === "states"
                        ? []
                        : filterName === "companies" // Updated to handle companies
                            ? []
                            : filterName === "financialYear"
                                ? null
                                : undefined
            );
        }
    };

    useEffect(() => {
        if (isRemoving.current) {
            applyFilters();
            isRemoving.current = false;
        }
    }, [filters, applyFilters]);

    const handleClose = (event, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
        setTokenOpen(false);
    };

    // Function to transform data for graph
    function transformBenefitsDataForGraph() {
        const allData = [];

        // Add state data with type prefix
        if (reportData?.by_state && reportData.by_state.length > 0) {
            reportData.by_state.forEach(item => {
                if (item.state_name !== "SUB TOTAL") { // Skip subtotal row
                    allData.push({
                        ...item,
                        category_name: `State: ${item.state_name}`,
                        category_type: 'state'
                    });
                }
            });
        }

        // Add store data with type prefix
        if (reportData?.by_store && reportData.by_store.length > 0) {
            reportData.by_store.forEach(item => {
                if (item.store_name !== "SUB TOTAL") { // Skip subtotal row
                    allData.push({
                        ...item,
                        category_name: `Store: ${item.store_name}`,
                        category_type: 'store'
                    });
                }
            });
        }

        // Add company data with type prefix
        if (reportData?.by_company && reportData.by_company.length > 0) {
            reportData.by_company.forEach(item => {
                if (item.company_name !== "SUB TOTAL") { // Skip subtotal row
                    allData.push({
                        ...item,
                        category_name: `Company: ${item.company_name}`,
                        category_type: 'company'
                    });
                }
            });
        }

        if (allData.length === 0) return { categories: [], series: [] };

        // Extract categories
        const categories = allData.map(item => item.category_name);

        // Define keys to exclude from series
        const excludeKeys = [
            "total_sales",
            "birthday_redeemed_percentage",
            "cashback_redeemed_percentage",
            "state_name",
            "store_name",
            "company_name",
            "category_name",
            "category_type"
        ];

        // Get all numeric fields except excluded keys
        const numericKeys = Object.keys(allData[0]).filter(
            key => !excludeKeys.includes(key)
        );

        // Build Highcharts series
        const series = numericKeys.map(key => ({
            name: key
                .replace(/_/g, " ") // Replace underscores with spaces
                .replace(/\b\w/g, c => c.toUpperCase()), // Capitalize words
            data: allData.map(item => {
                // Convert string to number if necessary
                const value = item[key];
                return typeof value === 'string' ? parseFloat(value) : value;
            }),
        }));

        return { categories, series };
    }

    const { categories, series } = transformBenefitsDataForGraph();

    return (
        <div>
            <Grid container spacing={2}>
                <Grid item md={8} xs={12}>
                    <Header>
                        <h1>Redeemed Benefits Report</h1>
                    </Header>
                </Grid>
                <Grid
                    item
                    md={4}
                    xs={12}
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    <Button color="primary" variant="contained" onClick={() => setFilterOpen(!filterOpen)}>
                        Filter <FilterList style={{ marginLeft: "5px" }} fontSize="small" />
                    </Button>
                </Grid>

                <ReportFilter
                    open={filterOpen}
                    filters={filters}
                    onFilterChange={updateFilter}
                    onApply={handleFilterSubmit}
                    onReset={handleFilterReset}
                // showCompaniesFilter={true} // Added companies filter
                />

                {hasActiveFilters && (
                    <ActiveFilters
                        filters={appliedFilters}
                        onRemoveFilter={removeFilter}
                    />
                )}

                <Grid item xs={12}>
                    <VisualizationGraph
                        title={`Redeemed Benefits Report ${title}`}
                        subtitle={"Point and Sales"}
                        categories={categories}
                        series={series}
                        loading={loading}
                    />
                </Grid>

                {/* State Table - Only show if data exists */}
                {reportData?.by_state && reportData.by_state.length > 0 && (
                    <Grid item xs={12} mb={4}>
                        <Typography variant="h6" gutterBottom>
                            Redeemed Benefits by State
                        </Typography>
                        <ReportTable
                            columns={stateColumns}
                            rows={reportData.by_state}
                            loading={loading}
                            reportView="state"
                        />
                    </Grid>
                )}

                {/* Store Table - Only show if data exists */}
                {reportData?.by_store && reportData.by_store.length > 0 && (
                    <Grid item xs={12} mb={4}>
                        <Typography variant="h6" gutterBottom>
                            Redeemed Benefits by Store
                        </Typography>
                        <ReportTable
                            columns={storeColumns}
                            rows={reportData.by_store}
                            loading={loading}
                            reportView="store"
                        />
                    </Grid>
                )}

                {/* Company Table - Only show if data exists */}
                {reportData?.by_company && reportData.by_company.length > 0 && (
                    <Grid item xs={12} mb={4}>
                        <Typography variant="h6" gutterBottom>
                            Redeemed Benefits by Company
                        </Typography>
                        <ReportTable
                            columns={companyColumns}
                            rows={reportData.by_company}
                            loading={loading}
                            reportView="company"
                        />
                    </Grid>
                )}

                {/* Show message if no data is available */}
                {!loading &&
                    (!reportData?.by_state || reportData.by_state.length === 0) &&
                    (!reportData?.by_store || reportData.by_store.length === 0) &&
                    (!reportData?.by_company || reportData.by_company.length === 0) && (
                        <Grid item xs={12}>
                            <Typography align="center" variant="body1">
                                No data available for the selected filters.
                            </Typography>
                        </Grid>
                    )}

                <Footer overlay={overlay || props.overlayNew} />
            </Grid>

            <Snackbar
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={open || tokenOpen}
                onClose={handleClose}
            >
                <Alert
                    onClose={handleClose}
                    severity={messageState || tokenMessageState}
                    sx={{ width: "100%" }}
                >
                    {message || tokenMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ReedemedBenefits;